// VIP管理服務API接口
export interface VIPOwner {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  company?: string;
}

export interface VIPVehicle {
  licensePlate: string;
  brand?: string;
  model?: string;
  color?: string;
  isActive: boolean;
  addedAt: string;
}

export interface VIPParking {
  _id: string;
  parkingSpot: {
    _id: string;
    spotNumber: string;
    floor: number;
    zone: string;
    type: string;
    status: string;
  };
  owner: VIPOwner;
  vehicles: VIPVehicle[];
  vipLevel: 'platinum' | 'gold' | 'silver';
  validFrom: string;
  validTo: string;
  privileges: string[];
  status: 'active' | 'expired' | 'suspended';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    _id: string;
    username: string;
  };
  updatedBy?: {
    _id: string;
    username: string;
  };
}

export interface ParkingSpot {
  _id: string;
  spotNumber: string;
  floor: number;
  zone: string;
  type: string;
  status: string;
}

export interface VIPFilters {
  page?: number;
  limit?: number;
  status?: string;
  vipLevel?: string;
  search?: string;
}

export interface VIPCreateData {
  parkingSpotId: string;
  owner: VIPOwner;
  vehicles: Omit<VIPVehicle, 'isActive' | 'addedAt'>[];
  vipLevel: 'platinum' | 'gold' | 'silver';
  validFrom: string;
  validTo: string;
  privileges: string[];
  notes?: string;
}

export interface VIPUpdateData {
  owner?: VIPOwner;
  vehicles?: VIPVehicle[];
  vipLevel?: 'platinum' | 'gold' | 'silver';
  validFrom?: string;
  validTo?: string;
  privileges?: string[];
  status?: 'active' | 'expired' | 'suspended';
  notes?: string;
}

export interface VIPResponse {
  success: boolean;
  data: VIPParking[];
  pagination: {
    current: number;
    total: number;
    count: number;
    totalRecords: number;
  };
}

export interface VIPDetailResponse {
  success: boolean;
  data: VIPParking;
}

export interface AvailableSpotsResponse {
  success: boolean;
  data: ParkingSpot[];
}

export interface ExpiringVIPsResponse {
  success: boolean;
  data: VIPParking[];
  count: number;
}

class VIPService {
  private baseURL = `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/vip`;

  // 添加授權標頭（容錯讀取 token/authToken，localStorage 與 sessionStorage）
  private getAuthHeaders(): Record<string, string> {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  // 獲取VIP車位列表
  async getVIPParkings(filters: VIPFilters = {}): Promise<VIPResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.vipLevel) params.append('vipLevel', filters.vipLevel);
    if (filters.search) params.append('search', filters.search);

    const response = await fetch(`${this.baseURL}?${params}`, {
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }

  // 根據ID獲取VIP車位詳情
  async getVIPParkingById(id: string): Promise<VIPDetailResponse> {
    const response = await fetch(`${this.baseURL}/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }

  // 創建新的VIP車位
  async createVIPParking(data: VIPCreateData): Promise<VIPDetailResponse> {
    const response = await fetch(this.baseURL, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  }

  // 更新VIP車位
  async updateVIPParking(id: string, data: VIPUpdateData): Promise<VIPDetailResponse> {
    const response = await fetch(`${this.baseURL}/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return response.json();
  }

  // 刪除VIP車位
  async deleteVIPParking(id: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseURL}/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }

  // 添加車輛到VIP車位
  async addVehicle(id: string, vehicleData: Omit<VIPVehicle, 'isActive' | 'addedAt'>): Promise<VIPDetailResponse> {
    const response = await fetch(`${this.baseURL}/${id}/vehicles`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(vehicleData),
    });
    return response.json();
  }

  // 從VIP車位移除車輛
  async removeVehicle(id: string, licensePlate: string): Promise<VIPDetailResponse> {
    const response = await fetch(`${this.baseURL}/${id}/vehicles/${licensePlate}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }

  // 啟用/停用車輛
  async toggleVehicle(id: string, licensePlate: string, isActive: boolean): Promise<VIPDetailResponse> {
    const response = await fetch(`${this.baseURL}/${id}/vehicles/${licensePlate}/toggle`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    });
    return response.json();
  }

  // 根據車牌查找VIP車位
  async lookupByLicensePlate(licensePlate: string): Promise<VIPDetailResponse> {
    const response = await fetch(`${this.baseURL}/lookup/${licensePlate}`, {
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }

  // 獲取即將過期的VIP車位
  async getExpiringVIPs(days: number = 7): Promise<ExpiringVIPsResponse> {
    const response = await fetch(`${this.baseURL}/expiring/${days}`, {
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }

  // 獲取可用的停車位
  async getAvailableSpots(): Promise<AvailableSpotsResponse> {
    const response = await fetch(`${this.baseURL}/available-spots`, {
      headers: this.getAuthHeaders(),
    });
    return response.json();
  }
}

export const vipService = new VIPService();
export default vipService;